import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { env } from '../../../../env';
import { SignatureDocument } from '../../../../model';
import { GlobalActions } from '../../../../model/global-actions';
import { retrieveSignatureDocument } from '../../../../store/features/user-access/user-registration/user-registration-thunk';
import { UserRegistrationState } from '../../../../store/features/user-access/user-registration/user-registration-types';
import { useAppSelector } from '../../../redux';
import appRoutes from '../../../../routing/app-routes';
import {
  resetSignatureDocumentState,
  retrieveBundleUrlForSignature,
  selectSignRoomState,
  signRoomSliceActions,
  SignRoomState,
} from '../../../../store';
import { useIntl } from 'react-intl';
import { ErrorMessages } from '../../../../model/error';
import { commonErrorMessages } from '../../../../messages';

export interface UserRegistrationSignatureAreaLogicCallbacks {
  selectedOptionUserType: string;
  document: SignatureDocument;
  isLoading: boolean;
  signIsExpired: boolean;
  onAheadButtonPress: () => void;
  handleGoBack: () => void;
}

export const useUserRegistrationSignatureAreaLogic = (
  actions: GlobalActions
): UserRegistrationSignatureAreaLogicCallbacks => {
  const dispatch = useDispatch();
  const { navigate, showToast, closeToast } = actions;
  const { formatMessage } = useIntl();

  const {
    signatureDocument,
    selectedOptionUserType,
    retrieveSignatureDocumentRequestStatus,
    bundleId,
    tmpToken,
    userCode,
  } = useAppSelector(
    (state) => state.features.userRegistration as UserRegistrationState
  );

  const {
    bundleUrlToSignError,
    bundleUrlToSignRequestStatus,
    bundleUrlToSign,
  } = useAppSelector<SignRoomState>(selectSignRoomState);

  const [signIsExpired, setSignIsExpired] = useState(false);

  useEffect(() => {
    if (!signatureDocument && userCode && bundleId && tmpToken) {
      dispatch(retrieveSignatureDocument());
    }
  }, [signatureDocument, userCode, bundleId, tmpToken, dispatch]);

  const onAheadButtonPress = useCallback(() => {
    dispatch(retrieveBundleUrlForSignature({ bundleId, token: tmpToken }));
  }, [bundleId, tmpToken, dispatch]);

  useEffect(() => {
    if (bundleUrlToSignError) {
      const errorMessages: ErrorMessages = {
        title: commonErrorMessages.errorTitleDefault,
        description: commonErrorMessages.errorDescriptionDefault,
      };
      showToast({
        type: 'error',
        title: formatMessage(errorMessages.title),
        description:
          errorMessages.description && formatMessage(errorMessages.description),
        close: closeToast,
      });
      dispatch(signRoomSliceActions.clearBundleUrlToSignError());
    }
  }, [dispatch, bundleUrlToSignError, showToast, formatMessage, closeToast]);

  useEffect(() => {
    if (bundleUrlToSignRequestStatus === 'succeeded') {
      dispatch(signRoomSliceActions.clearBundleUrlToSignError());
      if (bundleUrlToSign) {
        if (env.appModule === 'web') {
          window.location.href = bundleUrlToSign;
        } else {
          navigate(appRoutes.SignRoomPage, {
            state: { from: appRoutes.SignatureAreaUserRegistration },
          });
        }
      } else {
        setSignIsExpired(true);
      }
    }
  }, [bundleUrlToSignRequestStatus, bundleUrlToSign, navigate, dispatch]);

  const handleGoBack = useCallback(() => {
    navigate(-1);
    dispatch(resetSignatureDocumentState());
  }, [dispatch, navigate]);

  return {
    document: signatureDocument,
    selectedOptionUserType,
    isLoading: retrieveSignatureDocumentRequestStatus === 'loading',
    signIsExpired,
    onAheadButtonPress,
    handleGoBack,
  };
};
